import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  MessageSquare, Phone, Mail, User, RefreshCw, 
  ChevronRight, Clock, Bot, UserCircle, X,
  AlertCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [error, setError] = useState(null);

  const fetchConversations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/admin/conversations`);
      if (response.data.error) {
        setError(response.data.error);
        setConversations([]);
      } else {
        setConversations(response.data.conversations || []);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to fetch conversations');
      toast.error('Failed to fetch conversations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const formatTimestamp = (unixSecs) => {
    if (!unixSecs) return 'N/A';
    const date = new Date(unixSecs * 1000);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startUnix, endUnix) => {
    if (!startUnix || !endUnix) return 'N/A';
    const duration = endUnix - startUnix;
    const mins = Math.floor(duration / 60);
    const secs = duration % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done':
        return 'bg-green-500/20 text-green-400';
      case 'in-progress':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'failed':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-white/10 text-white/60';
    }
  };

  const ConversationCard = ({ conv }) => {
    const hasContact = conv.contact_info?.name || conv.contact_info?.email || conv.contact_info?.phone;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => setSelectedConversation(conv)}
        className={`p-4 rounded-xl border cursor-pointer transition-all ${
          selectedConversation?.conversation_id === conv.conversation_id
            ? 'bg-[#D4AF37]/10 border-[#D4AF37]/50'
            : 'bg-[#111] border-white/10 hover:border-white/20'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              {hasContact ? (
                <User size={18} className="text-[#D4AF37]" />
              ) : (
                <MessageSquare size={18} className="text-[#D4AF37]" />
              )}
            </div>
            <div>
              <p className="text-white font-medium">
                {conv.contact_info?.name || 'Unknown Visitor'}
              </p>
              <p className="text-white/40 text-xs">
                {formatTimestamp(conv.start_time)}
              </p>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(conv.status)}`}>
            {conv.status}
          </span>
        </div>

        {/* Contact Info Preview */}
        <div className="space-y-1 mb-3">
          {conv.contact_info?.email && (
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Mail size={12} />
              <span>{conv.contact_info.email}</span>
            </div>
          )}
          {conv.contact_info?.phone && (
            <div className="flex items-center gap-2 text-sm text-white/60">
              <Phone size={12} />
              <span>{conv.contact_info.phone}</span>
            </div>
          )}
        </div>

        {/* Transcript Preview */}
        {conv.transcript && conv.transcript.length > 0 && (
          <p className="text-white/50 text-sm line-clamp-2">
            {conv.transcript[0]?.message || conv.transcript[0]?.content || 'No message'}
          </p>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Clock size={12} />
            <span>{formatDuration(conv.start_time, conv.end_time)}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#D4AF37]">
            <span>View transcript</span>
            <ChevronRight size={14} />
          </div>
        </div>
      </motion.div>
    );
  };

  const TranscriptDetail = ({ conv }) => {
    if (!conv) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-[#111] border border-white/10 rounded-xl overflow-hidden h-full flex flex-col"
      >
        {/* Header with Contact Info */}
        <div className="p-5 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Conversation Details</h3>
            <button
              onClick={() => setSelectedConversation(null)}
              className="p-1 rounded-lg hover:bg-white/10 text-white/50 hover:text-white lg:hidden"
            >
              <X size={20} />
            </button>
          </div>

          {/* Contact Info Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-black/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <User size={14} className="text-[#D4AF37]" />
                <span className="text-xs text-white/50">Name</span>
              </div>
              <p className="text-white font-medium truncate">
                {conv.contact_info?.name || 'Not provided'}
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Mail size={14} className="text-[#D4AF37]" />
                <span className="text-xs text-white/50">Email</span>
              </div>
              <p className="text-white font-medium truncate">
                {conv.contact_info?.email || 'Not provided'}
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Phone size={14} className="text-[#D4AF37]" />
                <span className="text-xs text-white/50">Phone</span>
              </div>
              <p className="text-white font-medium truncate">
                {conv.contact_info?.phone || 'Not provided'}
              </p>
            </div>
          </div>

          {/* Meta Info */}
          <div className="flex items-center gap-4 mt-4 text-sm text-white/50">
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{formatTimestamp(conv.start_time)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>Duration: {formatDuration(conv.start_time, conv.end_time)}</span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(conv.status)}`}>
              {conv.status}
            </span>
          </div>
        </div>

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto p-5">
          <h4 className="text-sm font-semibold text-white/70 mb-4 flex items-center gap-2">
            <MessageSquare size={16} />
            Full Transcript
          </h4>
          
          <div className="space-y-4">
            {conv.transcript && conv.transcript.length > 0 ? (
              conv.transcript.map((entry, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${entry.role === 'agent' ? '' : 'flex-row-reverse'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    entry.role === 'agent' 
                      ? 'bg-[#D4AF37]/20' 
                      : 'bg-blue-500/20'
                  }`}>
                    {entry.role === 'agent' ? (
                      <Bot size={16} className="text-[#D4AF37]" />
                    ) : (
                      <UserCircle size={16} className="text-blue-400" />
                    )}
                  </div>
                  <div className={`flex-1 ${entry.role === 'agent' ? '' : 'text-right'}`}>
                    <p className="text-xs text-white/40 mb-1">
                      {entry.role === 'agent' ? 'AI Assistant' : 'Visitor'}
                    </p>
                    <div className={`inline-block max-w-[85%] p-3 rounded-xl ${
                      entry.role === 'agent'
                        ? 'bg-white/5 text-white/80 rounded-tl-none'
                        : 'bg-blue-500/20 text-white rounded-tr-none'
                    }`}>
                      <p className="text-sm leading-relaxed">
                        {entry.message || entry.content || 'No content'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-white/40">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                <p>No transcript available</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Phone className="text-[#D4AF37]" />
            Voice Conversations
          </h2>
          <p className="text-sm text-white/50">ElevenLabs AI Assistant transcripts</p>
        </div>
        
        <button
          onClick={fetchConversations}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white/70 rounded-lg hover:bg-white/10 transition-colors"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Error loading conversations</p>
              <p className="text-red-400/70 text-sm mt-1">{error}</p>
              {error.includes('permission') && (
                <div className="mt-3 p-3 bg-black/30 rounded-lg">
                  <p className="text-white/70 text-sm font-medium mb-2">How to fix:</p>
                  <ol className="text-white/60 text-sm space-y-1 list-decimal list-inside">
                    <li>Go to <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">ElevenLabs API Keys</a></li>
                    <li>Create a new API key</li>
                    <li>Enable "Conversational AI Read" permission</li>
                    <li>Update the ELEVENLABS_API_KEY in backend/.env</li>
                    <li>Restart the backend server</li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {conversations.length === 0 && !error ? (
        <div className="bg-[#111] border border-white/10 rounded-xl p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={32} className="text-[#D4AF37]/50" />
          </div>
          <h3 className="text-white font-semibold mb-2">No Conversations Yet</h3>
          <p className="text-white/50 text-sm max-w-md mx-auto">
            Conversations with the AI voice assistant will appear here once visitors start interacting with it.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Conversations List */}
          <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2">
            <p className="text-sm text-white/50 mb-2">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</p>
            {conversations.map((conv) => (
              <ConversationCard key={conv.conversation_id} conv={conv} />
            ))}
          </div>

          {/* Transcript Detail */}
          <div className={`${selectedConversation ? '' : 'hidden lg:block'}`}>
            {selectedConversation ? (
              <TranscriptDetail conv={selectedConversation} />
            ) : (
              <div className="bg-[#111] border border-white/10 rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <ChevronRight size={48} className="text-white/20 mb-4" />
                <p className="text-white/40">Select a conversation to view the full transcript</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConversations;
